import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { merge } from "lodash";

import { useAppSelector } from "app/hooks";
import ColorPickerField from "components/color-picker-field";
import SelectProvider from "components/custom-filters-list/filters/select-provider.component";
import DateStringFormField from "components/date-string-form-field";
import {
  DEFAULT_DATE_QUICK_OPTIONS,
  defaultOnQuickOptionClick
} from "components/date-string-form-field/date-string-form-field.util";
import InputFormField from "components/input-form-field";
import MentionSaveHint from "components/mentions/mention-save-hint.component";
import { MENTION_SOLID_CONTENT_STYLE } from "components/mentions/mentions.util";
import useMentionEditor from "components/mentions/use-mention-editor.hook";
import ToggleButtonGroupFormField from "components/toggle-button-group-form-field/toggle-button-group-form-field.component";
import WordProcessorField from "components/word-processor-field";
import {
  STAFF_TASK_COLORS_FEATURE,
  TASK_TEMPLATE_RICH_TEXT_FEATURE,
  TEAMS_FEATURE,
  TEAM_MENTIONS_FEATURE
} from "constants/feature-flags";
import { TASK_TITLE_MAX_LENGTH } from "constants/staff-tasks";
import { TASK_TEMPLATE_TYPES } from "constants/templates";
import { selectCurrentClinic } from "features/auth/auth-slice";

import PatientFieldWithSearch from "../../../components/patient-field-with-search";
import { useGetPatientQuery } from "../../../services/patients";
import { useStaffTaskRequiredFields } from "../hooks/use-staff-task-required-fields.hook";
import useStaffTasksV2 from "../hooks/use-staff-tasks-v2.hook";
import {
  EMPTY_DEFAULT_TASK_VALUES,
  type StaffTaskV2FormValues,
  getTaskPriorityOptions,
  validateTaskPriority
} from "../staff-task.utils";
import StaffTaskAttachments, {
  type StaffTaskAttachmentsRef
} from "./staff-task-modal-dialog/staff-task-attachments";
import TeamSelectFormField from "./team-select-form-field.component";

export default function StaffTaskForm() {
  const attachmentsRef = useRef<StaffTaskAttachmentsRef>(null);
  const flags = useFlags();
  // SN-17576: when on, the task description uses the full "default" rich editor
  // (checkboxes, links, formatting) instead of the plain textOnly editor.
  const enableTaskRichText = Boolean(flags[TASK_TEMPLATE_RICH_TEXT_FEATURE]);
  // @mentions in the description: the backend notifies the people whose pills are in it on Save.
  const enableMentions = Boolean(flags[TEAM_MENTIONS_FEATURE]);
  // Teams: "Send to a team" beside the assignee. A team task needs no assignee (it is Unclaimed).
  const enableTeams = Boolean(flags[TEAMS_FEATURE]);
  const currentClinicId = useAppSelector(selectCurrentClinic)?.id;
  const mentionEditorProps = useMentionEditor({
    enabled: enableMentions,
    clinicId: currentClinicId,
    contentStyle: MENTION_SOLID_CONTENT_STYLE,
    // A team pill in the description reaches every member on save (TEAMS_FEATURE).
    includeTeams: enableMentions && enableTeams
  });

  const {
    handleOpenTaskDrawer,
    handleCreate,
    isUpdating,
    isPatientViewMode: isDrawerPatientViewMode,
    patientId,
    taskPrefill
  } = useStaffTasksV2();

  const { data: patient } = useGetPatientQuery(
    {
      patientId: Number(patientId),
      query: "{id,first_name,middle_name,last_name}"
    },
    {
      skip: !patientId || patientId === -1
    }
  );

  const defaultValues: StaffTaskV2FormValues = useMemo(() => {
    // lodash merge skips undefined source values, so an absent prefill field
    // leaves the EMPTY_DEFAULT_TASK_VALUES default untouched.
    return merge({}, EMPTY_DEFAULT_TASK_VALUES, {
      patient: patient || null,
      title: taskPrefill?.title,
      description: taskPrefill?.description,
      due_at: taskPrefill?.due_at,
      assignee: taskPrefill?.assignee
    });
  }, [patient, taskPrefill]);

  const formMethods = useForm<StaffTaskV2FormValues>({
    mode: "onBlur",
    defaultValues
  });

  const {
    watch,
    control,
    reset,
    setValue,
    handleSubmit,
    formState: { isValid, errors }
  } = formMethods;

  const { isDueDateRequired, isPriorityRequired } =
    useStaffTaskRequiredFields();
  const priorityOptions = useMemo(
    () => getTaskPriorityOptions(isPriorityRequired),
    [isPriorityRequired]
  );

  const color = useWatch({ control, name: "color" });

  const assignee = useWatch({ control, name: "assignee" });
  const team = useWatch({ control, name: "team" });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = useCallback(
    (data: StaffTaskV2FormValues) => {
      handleCreate(data, async (createdTaskId) => {
        if (createdTaskId && attachmentsRef.current?.hasPendingFiles()) {
          await attachmentsRef.current.uploadPendingFiles(createdTaskId);
        }
      });
    },
    [handleCreate]
  );

  return (
    <Box sx={{ pt: 2 }} component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          mb: 1,
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontSize: "24px"
          }}
        >
          New Task
        </Typography>
      </Stack>
      <Grid
        item
        container
        xs={12}
        spacing={2}
        sx={{ mb: 3, width: "100%", pl: 3, pr: 1 }}
      >
        <Grid item xs={12}>
          <ToggleButtonGroupFormField
            name="priority"
            label="Priority"
            options={priorityOptions}
            control={control}
            required
            errors={errors}
            rules={{ validate: validateTaskPriority(isPriorityRequired) }}
          />
        </Grid>
        {flags[STAFF_TASK_COLORS_FEATURE] && (
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ColorPickerField
                control={control}
                label="Task Color"
                zIndex={14001}
              />
              {color && (
                <Button
                  size="small"
                  onClick={() => setValue("color", null)}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Clear
                </Button>
              )}
            </Stack>
          </Grid>
        )}
        <Grid item xs={12}>
          <InputFormField
            name="title"
            label="Title"
            control={control}
            required
            autoFocus
            inputProps={{ maxLength: TASK_TITLE_MAX_LENGTH }}
            characterLimit={TASK_TITLE_MAX_LENGTH}
            sx={{ ml: 0 }}
          />
        </Grid>
        <Grid item xs={12}>
          <SelectProvider
            isActive={true}
            name="assignee"
            label={enableTeams ? "Assign to a person" : "Assignee"}
            includeOtherClinics
            ignoreRegionsFilter
            ignoreClinicsFilter
            control={control}
            required={!(enableTeams && team?.value)}
            sx={{ ml: 0 }}
          />
        </Grid>
        {enableTeams && (
          <Grid item xs={12}>
            <TeamSelectFormField
              name="team"
              control={control}
              onOptionSelected={(picked) => {
                // Sent to a team: nobody owns it until a member takes it.
                if (picked)
                  setValue("assignee", null, { shouldValidate: true });
              }}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <SelectProvider
            isActive
            name="assignees"
            label="Additional Assignees"
            includeOtherClinics
            ignoreRegionsFilter
            ignoreClinicsFilter
            control={control}
            multiple
            enableSelectAll
            excludeSelectAllValues={assignee?.value}
            sx={{ ml: 0 }}
          />
        </Grid>
        <Grid item xs={12}>
          <PatientFieldWithSearch
            label="Patient"
            control={control}
            required={isDrawerPatientViewMode}
          />
        </Grid>
        <Grid item xs={12}>
          <DateStringFormField
            name="due_at"
            label="Due Date"
            required={isDueDateRequired}
            control={control}
            PopperProps={{
              style: { zIndex: 14001 }
            }}
            quickOptions={DEFAULT_DATE_QUICK_OPTIONS}
            onQuickOptionClick={defaultOnQuickOptionClick}
            sx={{ ml: 0, pr: 0, minWidth: "100%", "& > div": { flexGrow: 1 } }}
          />
        </Grid>

        <Grid item xs={12}>
          <WordProcessorField
            name="description"
            control={control}
            placeholder="Enter task details"
            height={280}
            featuresGroup={enableTaskRichText ? "default" : "textOnly"}
            allowTemplates
            allowMergeTags
            allowInsertMergeTagsValues
            templateTypes={TASK_TEMPLATE_TYPES}
            allowToolbarAutohide={false}
            allowUndoRedo={false}
            allowPasteAsText={false}
            mergeTagsPatientId={Number(watch("patient")?.id) || -1}
            {...mentionEditorProps}
          />
          {enableMentions && <MentionSaveHint />}
        </Grid>
        <Grid item xs={12}>
          <StaffTaskAttachments ref={attachmentsRef} taskId={undefined} />
        </Grid>
      </Grid>
      <Stack
        sx={{ pr: 3 }}
        justifyContent="flex-end"
        spacing={2}
        direction="row"
      >
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            handleOpenTaskDrawer("view");
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          disabled={!isValid}
          loading={isUpdating}
          variant="contained"
          size="small"
          type="submit"
        >
          Save
        </LoadingButton>
      </Stack>
    </Box>
  );
}
